import React, { useContext, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Layout from "../../components/layout/login";
import BackToLoginButton from "../../components/auth/BackToLoginButton";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import * as yup from "yup";
import { Formik, Form } from "formik";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import VpnKeyRoundedIcon from "@mui/icons-material/VpnKeyRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import styles from "./style.module.scss";
import { postData } from "../../services/ajax/";
import { LoadingModalContext } from "../../components/modal/loading";
import { setYupLocaleES } from "../../const/yup";

setYupLocaleES();

const schema = yup.object().shape({
  email: yup
    .string()
    .email("Ingresá un correo electrónico válido")
    .required("El correo es obligatorio"),
  cuit: yup
    .string()
    .required("El CUIT es obligatorio")
    .test(
      "cuit-11",
      "El CUIT son 11 números seguidos, sin espacios, puntos ni guiones.",
      (v) => (v == null ? false : String(v).replace(/\D/g, "").length === 11)
    ),
});

function CuitFormatHint() {
  return (
    <Typography component="span" variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, lineHeight: 1.5 }}>
      <strong>Solo números</strong>, 11 cifras en total: sin espacios, puntos ni guiones. Es el mismo
      CUIT que en AFIP o el que cargó el laboratorio al dar de alta tu usuario.
    </Typography>
  );
}

function RecuperarAccesoPage() {
  const theme = useTheme();
  const loadingModal = useContext(LoadingModalContext);
  const [apiError, setApiError] = useState("");
  const [okSent, setOkSent] = useState(false);

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    setApiError("");
    loadingModal.setOpen(true);
    setSubmitting(true);
    const cuil = parseInt(String(values.cuit).replace(/\D/g, ""), 10);
    const response = await postData("usuarios/recover", {
      email: String(values.email).trim(),
      cuil,
    });
    loadingModal.setOpen(false);
    setSubmitting(false);

    if (response.error) {
      setApiError(response.error);
      return;
    }

    setOkSent(true);
    resetForm();
  };

  if (okSent) {
    return (
      <Layout>
        <Grid
          container
          direction="column"
          className={styles.main}
          sx={{ alignItems: "stretch" }}
          role="status"
          aria-live="polite"
        >
          <Alert
            severity="success"
            variant="filled"
            icon={<CheckCircleRoundedIcon fontSize="inherit" />}
            sx={{ mb: 3, borderRadius: 2, py: 1.5 }}
          >
            <strong>Listo.</strong> Revisá tu casilla: te enviamos un correo con un enlace
            para restablecer la contraseña. Si no lo ves, buscá también en spam.
          </Alert>
          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            size="large"
            fullWidth
            sx={{
              py: 1.5,
              fontWeight: 700,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "1rem",
              background: (t) =>
                `linear-gradient(90deg, ${t.palette.primary.main} 0%, ${alpha(t.palette.primary.dark, 0.92)} 100%)`,
              boxShadow: (t) => `0 8px 24px ${alpha(t.palette.primary.main, 0.35)}`,
            }}
          >
            Volver a inicio de sesión
          </Button>
        </Grid>
      </Layout>
    );
  }

  return (
    <Layout>
      <Grid container direction="column" className={styles.main} sx={{ alignItems: "stretch" }}>
        <BackToLoginButton sx={{ mb: 2 }} />

        <Typography
          component="h2"
          variant="h5"
          align="center"
          sx={{ fontWeight: 700, mb: 0.5 }}
        >
          No podés acceder
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
          Acá podés pedir un correo para restablecer tu contraseña. Necesitamos que
          coincidan <strong>el CUIT y el mail</strong> con los que tiene cargado el
          laboratorio. El CUIT debe ingresarse <strong>solo con números</strong> (sin
          espacios, puntos ni guiones).
        </Typography>

        <Box
          className={styles.guidePanel}
          sx={{
            p: 2,
            background: (t) =>
              `linear-gradient(145deg, ${alpha(t.palette.primary.main, 0.07)} 0%, ${alpha(
                t.palette.info.main,
                0.06
              )} 100%)`,
            border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.2)}`,
          }}
        >
          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 1.5 }}>
            Cómo seguís, en 3 pasos
          </Typography>
          <div className={`${styles.step} ${styles.step1}`}>
            <Box
              sx={(t) => ({
                minWidth: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: t.palette.primary.main,
                color: t.palette.primary.contrastText,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontWeight: 800,
                flexShrink: 0,
                boxShadow: `0 0 0 2px ${alpha(t.palette.primary.main, 0.2)}`,
              })}
            >
              1
            </Box>
            <div>
              <Typography variant="body2" fontWeight={600}>
                Completá CUIT y correo
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Deben ser los mismos que figuran en el sistema. CUIT en 11 números, sin
                separadores.
              </Typography>
            </div>
          </div>
          <div className={`${styles.step} ${styles.step2}`}>
            <Box
              sx={{
                minWidth: 28,
                height: 28,
                borderRadius: "50%",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <MarkEmailReadRoundedIcon fontSize="small" />
            </Box>
            <div>
              <Typography variant="body2" fontWeight={600}>
                Revisá tu correo
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Incluida la carpeta de spam o correo no deseado.
              </Typography>
            </div>
          </div>
          <div className={`${styles.step} ${styles.step3}`}>
            <Box
              sx={{
                minWidth: 28,
                height: 28,
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <VpnKeyRoundedIcon fontSize="small" />
            </Box>
            <div>
              <Typography variant="body2" fontWeight={600}>
                Abrí el enlace y elegí una clave nueva
              </Typography>
              <Typography variant="caption" color="text.secondary">
                El enlace vence a las 24 horas. Después volvé a pedirlo desde acá.
              </Typography>
            </div>
          </div>
        </Box>

        {apiError ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {apiError}
          </Alert>
        ) : null}

        <Formik
          initialValues={{ email: "", cuit: "" }}
          validationSchema={schema}
          onSubmit={onSubmit}
        >
          {({ errors, touched, isSubmitting, values, handleChange, handleBlur, setFieldValue }) => (
            <Form className={styles.form} autoComplete="on">
              <TextField
                name="cuit"
                variant="outlined"
                label="CUIT (11 números, solo cifras)"
                placeholder="Ej. 20123456786"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={values.cuit}
                onChange={(e) => {
                  setApiError("");
                  const only = e.target.value.replace(/\D/g, "");
                  if (only.length <= 11) {
                    setFieldValue("cuit", only);
                  }
                }}
                onBlur={handleBlur("cuit")}
                error={Boolean(errors.cuit && touched.cuit)}
                helperText={errors.cuit && touched.cuit ? errors.cuit : <CuitFormatHint />}
                fullWidth
                disabled={isSubmitting}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
              <TextField
                name="email"
                variant="outlined"
                label="Correo electrónico"
                type="email"
                autoComplete="email"
                value={values.email}
                onChange={(e) => {
                  setApiError("");
                  handleChange(e);
                }}
                onBlur={handleBlur("email")}
                error={Boolean(errors.email && touched.email)}
                helperText={
                  errors.email && touched.email
                    ? errors.email
                    : "Escribí el mismo email que usás para ingresar al sistema."
                }
                fullWidth
                disabled={isSubmitting}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={isSubmitting}
                sx={{
                  py: 1.25,
                  fontWeight: 700,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  background: (t) =>
                    `linear-gradient(90deg, ${t.palette.primary.main} 0%, ${alpha(t.palette.primary.dark, 0.92)} 100%)`,
                  boxShadow: (t) => `0 8px 24px ${alpha(t.palette.primary.main, 0.35)}`,
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: (t) => `0 12px 28px ${alpha(t.palette.primary.main, 0.4)}`,
                  },
                }}
              >
                {isSubmitting ? "Enviando…" : "Enviarme el correo para restablecer"}
              </Button>
            </Form>
          )}
        </Formik>

        <div className={styles.linkPlain}>
          <RouterLink
            to="/login"
            style={{
              color: theme.palette.primary.main,
              fontWeight: 600,
              textDecoration: "none",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.textDecoration = "underline";
            }}
            onFocus={(e) => {
              e.currentTarget.style.textDecoration = "underline";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.textDecoration = "none";
            }}
            onBlur={(e) => {
              e.currentTarget.style.textDecoration = "none";
            }}
          >
            Recordé mi clave, volver al inicio de sesión
          </RouterLink>
        </div>
      </Grid>
    </Layout>
  );
}

export default RecuperarAccesoPage;
